import { useState } from 'react';
import CustomModalOverlay from '../../../../common/Custom/CustomModalOverlay/CustomModalOverlay';
import GlobalButton from '../../../../ui/GlobalButton';
import IntakeService from '../../../../../services/IntakeService';
import type { Intake, IntakeLink } from '@/types';
import { getErrorMessage } from '../../../../../utils/helpers/helpers';

interface DeleteIntakeLinkModalProps {
  open: boolean;
  close: () => void;
  intake: Intake | null;
  link: IntakeLink | null;
  refetch: () => Promise<void>;
}

export default function DeleteIntakeLinkModal({
  open,
  close,
  intake,
  link,
  refetch,
}: DeleteIntakeLinkModalProps) {
  const [deleting, setDeleting] = useState(false);
  
  const [errorMessage, setErrorMessage] = useState('');
  const [shaking, setShaking] = useState(false);

  const triggerShake = () => {
    setShaking(false);
    requestAnimationFrame(() => {
      requestAnimationFrame(() => setShaking(true));
    });
  };

  const handleClose = () => {
    setErrorMessage('');
    close();
  };

  const handleDelete = async () => {
    if (!intake?._id || !link?._id) {
      return;
    }

    setDeleting(true);
    setErrorMessage('');
    try {
      await IntakeService.deleteIntakeLink(intake._id, link._id);
      await refetch();
      handleClose();
    } catch (error) {
      console.error('Error deleting link:', error);
      setErrorMessage(getErrorMessage(error));
      triggerShake();
    } finally {
      setDeleting(false);
    }
  };

  return (
    <CustomModalOverlay
      open={open}
      onClose={handleClose}
      title={'Delete Link'}
      subTitle={'Are you sure you want to delete this link?'}
      width="600px"
    >
      <div className="flex flex-col gap-4 mt-4">
        <div className="text-sm">
          <span className="font-semibold text-text-primary-highlight">
            {link?.intake_link_name}
          </span>
          <br />
          <span className="text-blue-3 underline underline-offset-2">
            {link?.intake_link_url}
          </span>
        </div>
        {errorMessage && (
          <p
            className={`mt-2 text-sm text-error-2 ${shaking ? 'shake' : ''}`}
            onAnimationEnd={() => setShaking(false)}
          >
            {errorMessage}
          </p>
        )}
        <div className="flex justify-end gap-4 mb-4">
          <div>
            <GlobalButton variant="stroke" onClick={handleClose}>
              Cancel
            </GlobalButton>
          </div>
          <div>
            <GlobalButton
              variant="secondaryRed"
              state={deleting ? 'inactive' : 'default'}
              onClick={handleDelete}
              type={'submit'}
            >
              {deleting ? 'Deleting...' : 'Delete'}
            </GlobalButton>
          </div>
        </div>
      </div>
    </CustomModalOverlay>
  );
}
